/**
 * Pure utilities for guessing coordinate CRS from input values.
 * No side effects; all functions are deterministic and testable.
 */

import proj4 from "proj4";
import {
  WGS84_RANGES,
  UTM32_RANGES,
  UTM33_RANGES,
  CRS_EPSG,
  WGS84_PLAUSIBILITY_BBOX,
} from "../config/coordinateSearchConfig";

// Register UTM projections for proj4
proj4.defs(
  CRS_EPSG.UTM32,
  "+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs +type=crs",
);
proj4.defs(
  CRS_EPSG.UTM33,
  "+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs +type=crs",
);

/**
 * Safely parse a coordinate value string or number.
 * Returns the parsed number or NaN if parsing fails.
 */
export function parseCoordinatePair(a, b) {
  const numA = Number.parseFloat(String(a).replace(",", "."));
  const numB = Number.parseFloat(String(b).replace(",", "."));
  return { numA, numB };
}

/**
 * Check if a coordinate pair looks like valid WGS84 (latitude, longitude).
 * Checks both value ranges and plausibility within Germany bbox.
 */
export function looksLikeWgs84(lat, lon) {
  // Basic range checks
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lon) ||
    Math.abs(lat) > 90 ||
    Math.abs(lon) > 180
  ) {
    return false;
  }

  // Check if within plausible ranges for German territory
  return (
    lat >= WGS84_RANGES.latMin &&
    lat <= WGS84_RANGES.latMax &&
    lon >= WGS84_RANGES.lonMin &&
    lon <= WGS84_RANGES.lonMax
  );
}

/**
 * Check if a coordinate pair looks like valid UTM (easting, northing).
 * Performs a plausibility check: if we transform to WGS84, do the results land in Germany?
 * Returns { isPlausible, wgs84Coords } or null if transformation fails.
 */
export function tryUtmTransform(crs, x, y) {
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  try {
    const [lon, lat] = proj4(crs, CRS_EPSG.WGS84, [x, y]);

    // Check if transformed coordinates fall within Germany bounds (plausibility check)
    const isPlausible =
      lat >= WGS84_PLAUSIBILITY_BBOX.minLat &&
      lat <= WGS84_PLAUSIBILITY_BBOX.maxLat &&
      lon >= WGS84_PLAUSIBILITY_BBOX.minLon &&
      lon <= WGS84_PLAUSIBILITY_BBOX.maxLon;

    return {
      isPlausible,
      wgs84Coords: [lon, lat],
      lat,
      lon,
    };
  } catch (error) {
    // Transformation failed; coordinates are not valid for this CRS
    return null;
  }
}

/**
 * Check if UTM coordinates fall within the given zone's typical range.
 * This is a loose check to filter out obviously wrong inputs early.
 */
function isWithinUtmRange(x, y, crsRanges) {
  return (
    x >= crsRanges.easting.min &&
    x <= crsRanges.easting.max &&
    y >= crsRanges.northing.min &&
    y <= crsRanges.northing.max
  );
}

/**
 * Main CRS guessing function.
 * Given two coordinate values (a, b) that the user entered, attempt to determine
 * the most plausible CRS (WGS84, UTM32, or UTM33).
 *
 * Returns:
 *   {
 *     guessedCrs: string (EPSG code) or null,
 *     ambiguity: boolean (true if both UTM32 and UTM33 are plausible),
 *     guessStatus: string ('wgs84', 'utm32', 'utm33', 'ambiguous_utm', 'unknown'),
 *     wgs84Coords: [lon, lat] or null (final WGS84 result if guessed from UTM),
 *   }
 */
export function guessCoordinateCrs(a, b) {
  const { numA, numB } = parseCoordinatePair(a, b);

  // Both values must parse to finite numbers
  if (!Number.isFinite(numA) || !Number.isFinite(numB)) {
    return {
      guessedCrs: null,
      ambiguity: false,
      guessStatus: "unknown",
      wgs84Coords: null,
    };
  }

  // Try interpreting as WGS84 (latitude, longitude order)
  if (looksLikeWgs84(numA, numB)) {
    return {
      guessedCrs: CRS_EPSG.WGS84,
      ambiguity: false,
      guessStatus: "wgs84",
      wgs84Coords: [numB, numA], // [lon, lat]
    };
  }

  // Try interpreting as UTM (easting, northing order)
  // Check both zone 32 and 33

  const utm32Result = isWithinUtmRange(numA, numB, UTM32_RANGES)
    ? tryUtmTransform(CRS_EPSG.UTM32, numA, numB)
    : null;

  const utm33Result = isWithinUtmRange(numA, numB, UTM33_RANGES)
    ? tryUtmTransform(CRS_EPSG.UTM33, numA, numB)
    : null;

  const utm32Plausible = utm32Result?.isPlausible ?? false;
  const utm33Plausible = utm33Result?.isPlausible ?? false;

  // Case 1: Both UTM zones are plausible => ambiguous, fallback to UTM32
  if (utm32Plausible && utm33Plausible) {
    return {
      guessedCrs: CRS_EPSG.UTM32,
      ambiguity: true,
      guessStatus: "ambiguous_utm",
      wgs84Coords: utm32Result.wgs84Coords,
    };
  }

  // Case 2: Only UTM32 is plausible
  if (utm32Plausible) {
    return {
      guessedCrs: CRS_EPSG.UTM32,
      ambiguity: false,
      guessStatus: "utm32",
      wgs84Coords: utm32Result.wgs84Coords,
    };
  }

  // Case 3: Only UTM33 is plausible
  if (utm33Plausible) {
    return {
      guessedCrs: CRS_EPSG.UTM33,
      ambiguity: false,
      guessStatus: "utm33",
      wgs84Coords: utm33Result.wgs84Coords,
    };
  }

  // Case 4: Neither CRS looks plausible
  return {
    guessedCrs: null,
    ambiguity: false,
    guessStatus: "unknown",
    wgs84Coords: null,
  };
}
